import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ThemeProvider } from "next-themes";
import Index from "./pages/Index";
import Booking from "./pages/Booking";
import Progress from "./pages/Progress";
import Profile from "./pages/Profile";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import CoachPortal from "./pages/CoachPortal";
import AdminPortal from "./pages/AdminPortal";
import AdminCoachApplications from "./pages/AdminCoachApplications";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminCoachView from "./pages/AdminCoachView";
import CustomerDashboard from "./pages/CustomerDashboard";
import NotFound from "./pages/NotFound";
import ApplyAsCoach from "./pages/ApplyAsCoach";
import ProposalResponse from "./pages/ProposalResponse";
import BookingDetails from "./pages/BookingDetails";
import CoachBookingAction from "./pages/CoachBookingAction";
import ForCoaches from "./pages/ForCoaches";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <GoogleAnalytics />
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/apply-as-coach" element={
            <ProtectedRoute>
              <ApplyAsCoach />
            </ProtectedRoute>
          } />
          <Route path="/coach" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachPortal />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminPortal />
            </ProtectedRoute>
          } />
          <Route path="/admin/coach-applications" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminCoachApplications />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/coach/:coachId" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminCoachView />
            </ProtectedRoute>
          } />
          <Route element={<CustomerDashboard />}>
            <Route path="/booking" element={
              <ProtectedRoute allowedRoles={["user"]}>
                <Booking />
              </ProtectedRoute>
            } />
            <Route path="/progress" element={
              <ProtectedRoute allowedRoles={["user"]}>
                <Progress />
              </ProtectedRoute>
            } />
          </Route>
          <Route path="/proposal/:bookingId" element={
            <ProtectedRoute>
              <ProposalResponse />
            </ProtectedRoute>
          } />
          <Route path="/booking/:bookingId" element={
            <ProtectedRoute allowedRoles={["user"]}>
              <BookingDetails />
            </ProtectedRoute>
          } />
          <Route path="/coach/booking/:bookingId" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachBookingAction />
            </ProtectedRoute>
          } />
          <Route path="/for-coaches" element={<ForCoaches />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
